import {
    BrowserUtils,
    ComponentElements,
    ComponentInjector,
    ComponentObject,
    ComponentSelector,
    ComponentSelectors,
    ComponentService,
    Inject,
    UIComponent
} from "@atypon/ui-utils";

class Texts extends ComponentObject { }

class Selectors extends ComponentSelectors {
    options: string = '.test-widget__choice'
    orderOptions: string = '.test-widget__order-list__item'
    actionBtn = new ComponentSelector('.question_action')
    answer = new ComponentSelector('.client-side-test__answer')
    userChoices = new ComponentSelector('.client-side-test__user-choices')
    orderListWrapper = new ComponentSelector('.test-widget__order-list')
    freeTextInput = new ComponentSelector('#test-answer')
    freeTextCorrect = new ComponentSelector('.free-text-answer')
}

class ClassList extends ComponentObject { }

class Elements extends ComponentElements<Selectors> {
    actionBtn: HTMLElement
    answer: HTMLElement
    userChoices: HTMLElement
    orderBase: HTMLInputElement
    orderListWrapper: HTMLElement
    freeTextInput: HTMLInputElement
    freeTextCorrect: HTMLElement
}

enum questionType {
    multi_answer_questions = 'multi_answer_questions',
    single_answer_questions = 'single_answer_questions',
    reorder_answer_questions = 'reorder_answer_questions',
    free_text_questions = 'free_text_questions',
}

interface ClientSideTest extends ComponentService<Selectors, Elements, ClassList, Texts> { }

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class ClientSideTest implements UIComponent {

    @Inject()
    protected browserUtils:BrowserUtils;

    correctAnswers: string[] = []

    isCorrectOrder: boolean

    correctAnswersElements: HTMLElement[] = []

    selectedAnswers: string[] = []

    questionType: questionType

    isSingleOrMulti: boolean

    isCorrectAnswer: boolean

    isShowAnswerBtn: boolean

    sortable: null

    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this.elements.initialize(this.wrapper)
        this.getQuestionType()
        if (this.isSingleOrMulti) this.initMultiSingle()
        if (this.questionType === questionType.reorder_answer_questions) this.addEventListeners()
        if (this.questionType === questionType.free_text_questions) this.initFreeText()
    }

    protected getQuestionType(): void {
        this.questionType = questionType[this.wrapper.dataset.questionType]
        this.isSingleOrMulti = this.questionType === questionType.single_answer_questions
            || this.questionType === questionType.multi_answer_questions
    }

    addEventListeners(): void {
        const actionBtn = this.elements.actionBtn.querySelector('input')
        actionBtn && this.domUtils.addEventListener(actionBtn, 'click', (e) => {
            e.preventDefault()
            if (this.isSingleOrMulti) {
                this.elVisibility(this.elements.answer, null)
                this.setCorrectAnswer()
                this.showResult()
                this.addUserChoices()
                this.highlightCorrectAnswers()
            }
            if (this.questionType === questionType.reorder_answer_questions ) {
                this.setOrders()
                this.setCorrectAnswer()
                this.elVisibility(this.elements.answer, null)
                this.showResult()
            }
            if (this.questionType === questionType.free_text_questions) {
                this.setFreeTextCorrectAnswer()
                this.elVisibility(this.elements.answer, null)
                this.showResult()
            }
        })
    }

    protected initMultiSingle(): void {
        const options = this.domUtils.getElements<HTMLInputElement>(this.selectors.options)
        options.forEach((option: HTMLInputElement) => {
            this.getCorrectAnswers(option)
            this.domUtils.addEventListener(option, 'click', () => {
                this.getSelectedAnswers(option)
                this.isShowAnswerBtnReady()
                this.isShowAnswerBtn && this.elVisibility(this.elements.actionBtn, null)
                this.addEventListeners()
            })
        })
    }

    protected initFreeText(): void {
        this.addEventListeners()
    }

    protected setOrders(): void {
        const options = this.domUtils.getElements<HTMLElement>(this.selectors.orderOptions)
        this.selectedAnswers = []
        options.forEach(option => {
            this.selectedAnswers.push(option.dataset.value)
        })
        this.correctAnswers = [...this.selectedAnswers].sort()
    }

    protected setFreeTextCorrectAnswer(): void {
        this.isCorrectAnswer = this.elements.freeTextInput.value.toLowerCase()
            === this.elements.freeTextCorrect.innerText.toLowerCase()
    }

    protected isShowAnswerBtnReady(): void {
        const inputLength = this.questionType === questionType.multi_answer_questions ? 1 : 0
        this.isShowAnswerBtn = this.selectedAnswers.length > inputLength
    }

    protected getCorrectAnswers(option: HTMLElement): void {
        if (option.classList.contains('correct')) {
            this.correctAnswers.push(option.querySelector('input').value)
            this.correctAnswersElements.push(option)
        }
    }

    protected highlightCorrectAnswers(): void {
        !this.isCorrectAnswer && this.correctAnswersElements.length ?
            this.correctAnswersElements.forEach(el => el.classList.add('test-widget__choice--correct'))
            : this.correctAnswersElements.forEach(el => el.classList.remove('test-widget__choice--correct'))
    }

    protected getSelectedAnswers(option: HTMLInputElement): void {
        const $inputEl = option.querySelector('input')
        this.selectedAnswers = this.selectedAnswers.filter(item => item !== $inputEl.value)
        if (this.questionType === questionType.single_answer_questions) this.selectedAnswers = []
        if ($inputEl.checked) this.selectedAnswers.push($inputEl.value)
    }

    protected addUserChoices(): void {
        this.elements.userChoices.innerHTML = ` ${this.selectedAnswers.toString()}`
    }

    protected setCorrectAnswer(): void {
        if(this.isSingleOrMulti) {
            this.isCorrectAnswer = this.selectedAnswers.sort().toString() === this.correctAnswers.sort().toString();
            return;
        }
        this.isCorrectAnswer = this.selectedAnswers.toString() === this.correctAnswers.toString()
    }

    protected showResult(): void {
        const correctAnswer = this.elements.answer.querySelector('.correct-answer')
        const incorrectAnswer = this.elements.answer.querySelector('.incorrect-answer')
        this.isCorrectAnswer ? this.elVisibility(correctAnswer, incorrectAnswer) :
            this.elVisibility(incorrectAnswer, correctAnswer)
    }

    protected elVisibility(elShow, elHide): void {
        if (elShow) {
            elShow.classList.remove('hidden')
            elShow.removeAttribute('aria-hidden')
        }
        if (elHide) {
            elHide.classList.add('hidden')
            elHide.setAttribute('aria-hidden', 'true')
        }
    }
}

export default ClientSideTest